import { useEffect, useMemo } from "react";
import { Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_ROUTES, AUTH_ROUTES, ROUTE_NAMES } from "./routes";
import { USER_ROLES_IDS } from "./constants/common";
import Layout from "./pages/layout";
import Login from "./pages/auth/login";
import ProtectedRoute from "./pages/protectedRoute";
import Home from "./pages";
import ApproveVisitRequest from "./pages/approve-visit-request";
import SearchVisitRequest from "./pages/search-visit-request";
import CreateVisitRequest from "./pages/create-visit-request";
import Individual from "./pages/create-visit-request/individual";
import Group from "./pages/create-visit-request/group";
import VIP from "./pages/create-visit-request/vip";
import AddUsers from "./pages/add-users";
import ManageRoles from "./pages/manage-roles";
import SearchUsers from "./pages/search-users";
import NotFound from "./pages/not-found";
import useAuthStore from "./store/Auth";
import SystemCodes from "./pages/configuration/system-codes";
import VisitPurpose from "./pages/configuration/visit-purpose";
import Locations from "./pages/configuration/locations";
import EmailConfig from "./pages/configuration/email-config";
import CheckIn from "./pages/check-in";
import VisitRequestReport from "./pages/reports/visit-request-report";
import AccessLogReport from "./pages/reports/access-log-report";
import Departments from "./pages/configuration/departments";
import ActiveDirectoryConfig from "./pages/configuration/active-directory-config";
import DynamicWorkflowConfig from "./pages/configuration/dynamic-workflow-config";
import ExternalUsersReport from "./pages/reports/external-users-report";
import AuditLogsReport from "./pages/reports/audit-logs-report";
import BuildingsConfiguration from "./pages/configuration/buildings-configuration";
import ExternalHelpConfig from "./pages/configuration/external-help-config";
import GatesConfig from "./pages/configuration/gates-config";
import LoginUserReport from "./pages/configuration/login-user-report";
import VisitApprovedHostEmail from "./pages/email-templates/visit-approved-host-email";
import VisitSubmitted from "./pages/email-templates/visit-submitted";
import VisitRequestScheduleTemplate from "./pages/email-templates/visit-request-schedule-template";
import VisitRequestRejectedTemplate from "./pages/email-templates/visit-request-rejected-template";
import EmailToHostTemplate from "./pages/email-templates/email-to-host-template";
import VisitRejectedHostEmailTemplate from "./pages/email-templates/visit-rejected-host-email";
import ExternalPasswordReset from "./pages/email-templates/external-password-reset";
import ExternalVisitRequestCancelToHost from "./pages/email-templates/external-visit-request-cancel-to-host";
import ExternalVisitRequestCancelToVisitor from "./pages/email-templates/external-visit-request-cancel-to-visitor";
import CriticalNationalitiesConfig from "./pages/configuration/critical-nationalities-config";
import ExternalAccountRegistrationTemplate from "./pages/email-templates/external-account-registration-template";
import DeviceRejectedToStaffTemplate from "./pages/email-templates/device-rejected-to-staff-template";
import DeviceRejectedToVisitorTemplate from "./pages/email-templates/device-rejected-to-visitor-template";
import InternalConfig from "./pages/configuration/internal-config";
import AccessGroups from "./pages/configuration/access-groups";
import FRConfig from "./pages/configuration/fr-config";
import FRDevicesConfig from "./pages/configuration/devices-config";
import VisitorsReport from "./pages/reports/visitors-report";
import MeetingRoomsConfig from "./pages/configuration/meeting-rooms-config";
import VisitorCheckInTemplate from "./pages/email-templates/visitor-check-in-template";
import VisitorCheckOutTemplate from "./pages/email-templates/visitor-check-out-template";
import UnAuthorized from "./pages/unauthorized";
import OTP from "./pages/auth/otp";
import OTPTemplate from "./pages/email-templates/otp-template";
import { jwtDecode } from "jwt-decode";
import OverStayedVisitorsReport from "./pages/reports/overstayed-visitors-report";
import FrErrorsReport from "./pages/reports/fr-errors-report";
import RatingsReport from "./pages/reports/rating-report";
import InternalVisitRequestCancelToHost from "./pages/email-templates/internal-visit-request-cancel-to-host";
import InternalVisitRequestCancelToVisitor from "./pages/email-templates/internal-visit-request-cancel-to-visitor";
import AddBlacklistVisitor from "./pages/add-blacklist-visitor";
import SearchBlacklistVisitors from "./pages/search-blacklist-visitors";
import BlacklistingReport from "./pages/reports/blacklisting-report";
import SignInOidc from "./pages/signin-oidc";
import WarehouseInOutPermit from "./pages/permit-request/warehouse-in-out-permit";
import PcInOutPermit from "./pages/permit-request/pc-in-out-permit";
import SearchPermitRequest from "./pages/permit-request/search-permit-request";
import ApprovePermitRequest from "./pages/permit-request/approve-permit-request";
import StartEvacuation from "./pages/emergency-evacuation/start-evacuation";
import LiveEvacuationDashboard from "./pages/emergency-evacuation/live-evacuation-dashboard";
import MusterStation from "./pages/emergency-evacuation/muster-station";
import MusterPointsConfiguration from "./pages/configuration/muster-points-configuration";
import EvacuationEvents from "./pages/emergency-evacuation/evacuation-events";
import EvacuationReport from "./pages/emergency-evacuation/evacuation-report";
import EmergencyEvacuationTemplate from "./pages/email-templates/emergency-evacuation-template";
import PermitToWork from "./pages/permit-request/permit-to-work";
import LostVehicleStickerIdCard from "./pages/permit-request/lost-vehicle-sticker-id-card";
import ParkingStickerRequest from "./pages/permit-request/parking-sticker-request";
import EditProfile from "./pages/edit-profile";
import OfficeAccessAuthorization from "./pages/permit-request/office-access-authorization";
import IssuanceRenewalIdForStaffFromContractedCompanies from "./pages/permit-request/issuance-renewal-id-for-staff-from-contracted-companies";
import FinalPermitApprovalTemplate from "./pages/email-templates/final-permit-approval-template";
import SMSConfig from "./pages/configuration/sms-config";

function App() {
  const { i18n } = useTranslation();

  const { token } = useAuthStore();

  useEffect(() => {
    const body = document.body;
    if (i18n.language === "ar") {
      body.classList.add("font-droidkufi");
      body.classList.remove("font-lexend");
    } else {
      body.classList.add("font-lexend");
      body.classList.remove("font-droidkufi");
    }
  }, [i18n.language]);

  const decodedUser: any = useMemo(() => {
    if (token) {
      return jwtDecode(token);
    }
  }, [token]);

  return (
    <Routes>
      <Route path={AUTH_ROUTES.LOGIN} element={<Login />} />
      <Route path={AUTH_ROUTES.OTP} element={<OTP />} />

      <Route path={APP_ROUTES.NOT_FOUND} element={<NotFound />} />
      <Route path={APP_ROUTES.UNAUTHORIZED} element={<UnAuthorized />} />

      <Route path={AUTH_ROUTES.SIGNIN_OIDC} element={<SignInOidc />} />

      <Route
        path={APP_ROUTES.HOME}
        element={
          <ProtectedRoute
            routeName={
              Number(decodedUser?.RoleId) === USER_ROLES_IDS.ADMIN ||
                Number(decodedUser?.RoleId) === USER_ROLES_IDS.SECURITY ||
                Number(decodedUser?.RoleId) === USER_ROLES_IDS.SECURITY_MANAGER ||
                Number(decodedUser?.RoleId) === USER_ROLES_IDS.RECEPTION
                ? ROUTE_NAMES.HOME_SEARCH
                : Number(decodedUser?.RoleId) ===
                  USER_ROLES_IDS.DIRECT_MANAGER ||
                  Number(decodedUser?.RoleId) ===
                  USER_ROLES_IDS.INVENTORY_MANAGER
                  ? ROUTE_NAMES.APPROVE_PERMIT_REQUEST_SEARCH
                  : ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH
            }
          >
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Home */}
        <Route
          path={APP_ROUTES.HOME}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.HOME_SEARCH}>
              <Home />
            </ProtectedRoute>
          }
        />

        {/* Edit Profile */}
        <Route
          path={APP_ROUTES.EDIT_PROFILE}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EDIT_PROFILE_SEARCH}>
              <EditProfile />
            </ProtectedRoute>
          }
        />

        {/* Visitor Details */}
        <Route
          path={APP_ROUTES.CHECK_IN}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.HOME_SEARCH}>
              <CheckIn />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.CHECK_OUT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.HOME_SEARCH}>
              <CheckIn />
            </ProtectedRoute>
          }
        />

        {/* Visit Request */}
        <Route
          path={APP_ROUTES.APPROVE_VISIT_REQUEST}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH}
            >
              <ApproveVisitRequest />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.SEARCH_VISIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_SEARCH}>
              <SearchVisitRequest />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.CREATE_VISIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_CREATE}>
              <CreateVisitRequest />
            </ProtectedRoute>
          }
        />

        {/* Visit Request - Create */}
        <Route
          path={APP_ROUTES.INDIVIDUAL_VISIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_CREATE}>
              <Individual />
            </ProtectedRoute>
          }
        />
        <Route
          path={APP_ROUTES.GROUP_VISIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_CREATE}>
              <Group />
            </ProtectedRoute>
          }
        />
        <Route
          path={APP_ROUTES.VIP_VISIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_CREATE}>
              <VIP />
            </ProtectedRoute>
          }
        />

        {/* Approve Request */}
        <Route
          path={APP_ROUTES.APPROVE_REQUEST}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH}
            >
              <Individual />
            </ProtectedRoute>
          }
        />

        {/* Update Request */}
        <Route
          path={APP_ROUTES.UPDATE_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_SEARCH}>
              <Individual />
            </ProtectedRoute>
          }
        />

        {/* Permit Request */}
        <Route
          path={APP_ROUTES.SEARCH_PERMIT_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <SearchPermitRequest />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_PERMIT_REQUEST}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST_SEARCH}
            >
              <ApprovePermitRequest />
            </ProtectedRoute>
          }
        />

        {/* Warehouse In/Out Permit */}
        <Route
          path={APP_ROUTES.WAREHOUSE_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <WarehouseInOutPermit />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_WAREHOUSE_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <WarehouseInOutPermit />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_WAREHOUSE_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <WarehouseInOutPermit />
            </ProtectedRoute>
          }
        />

        {/* PC In/Out Permit */}
        <Route
          path={APP_ROUTES.PC_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <PcInOutPermit />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_PC_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <PcInOutPermit />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_PC_IN_OUT_PERMIT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <PcInOutPermit />
            </ProtectedRoute>
          }
        />

        {/* Lost Vehicle Sticker ID Card */}
        <Route
          path={APP_ROUTES.LOST_VEHICLE_STICKER_ID_CARD}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <LostVehicleStickerIdCard />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_LOST_VEHICLE_STICKER_ID_CARD}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <LostVehicleStickerIdCard />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_LOST_VEHICLE_STICKER_ID_CARD}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <LostVehicleStickerIdCard />
            </ProtectedRoute>
          }
        />

        {/* Parking Sticker Request */}
        <Route
          path={APP_ROUTES.PARKING_STICKER_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <ParkingStickerRequest />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_PARKING_STICKER_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <ParkingStickerRequest />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_PARKING_STICKER_REQUEST}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <ParkingStickerRequest />
            </ProtectedRoute>
          }
        />

        {/* Office Access Authorization */}
        <Route
          path={APP_ROUTES.OFFICE_ACCESS_AUTHORIZATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <OfficeAccessAuthorization />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_OFFICE_ACCESS_AUTHORIZATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <OfficeAccessAuthorization />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_OFFICE_ACCESS_AUTHORIZATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <OfficeAccessAuthorization />
            </ProtectedRoute>
          }
        />

        {/* Issuance or Renewal ID for Staff from Contracted Companies */}
        <Route
          path={APP_ROUTES.ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <IssuanceRenewalIdForStaffFromContractedCompanies />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <IssuanceRenewalIdForStaffFromContractedCompanies />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_ISSUANCE_RENEWAL_ID_FOR_STAFF_FROM_CONTRACTED_COMPANIES}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <IssuanceRenewalIdForStaffFromContractedCompanies />
            </ProtectedRoute>
          }
        />

        {/* Permit To Work */}
        <Route
          path={APP_ROUTES.PERMIT_TO_WORK}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <PermitToWork />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_PERMIT_TO_WORK}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.PERMIT_REQUEST_SEARCH}>
              <PermitToWork />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.APPROVE_PERMIT_TO_WORK}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.APPROVE_PERMIT_REQUEST}>
              <PermitToWork />
            </ProtectedRoute>
          }
        />

        {/* Users */}
        <Route
          path={APP_ROUTES.ADD_USERS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.USERS_CREATE}>
              <AddUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.SEARCH_USERS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.USERS_SEARCH}>
              <SearchUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.MANAGE_ROLES}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.ROLE_PRIVILEGES_SEARCH}>
              <ManageRoles />
            </ProtectedRoute>
          }
        />

        {/* Configurations */}
        <Route
          path={APP_ROUTES.INTERNAL_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.INTERNAL_CONFIG}>
              <InternalConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.SYSTEM_CODES}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.SYSTEM_CODES_SEARCH}>
              <SystemCodes />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_PURPOSE}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISIT_PURPOSE_SEARCH}>
              <VisitPurpose />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EMAIL_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMAIL_CONFIG_SEARCH}>
              <EmailConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.SMS_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.SMS_CONFIG_SEARCH}>
              <SMSConfig />
            </ProtectedRoute>
          }
        />

        {/* <Route
          path='/v/:id'
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.UPDATE_REQUEST_CREATE}>
              <Visit />
            </ProtectedRoute>
          }
        /> */}

        <Route
          path={APP_ROUTES.LOCATIONS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.LOCATIONS_SEARCH}>
              <Locations />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.GATES_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.GATES_SEARCH_CONFIG}>
              <GatesConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.DEPARTMENTS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.DEPARTMENTS_SEARCH}>
              <Departments />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.MEETING_ROOMS_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.MEETING_ROOMS_SEARCH_ONFIG}>
              <MeetingRoomsConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.ACCESS_GROUPS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.ACCESS_GROUPS_SEARCH}>
              <AccessGroups />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.ACTIVE_DIRECTORY_CONFIG}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.ACTIVE_DIRECTORY_CONFIG_SEARCH}
            >
              <ActiveDirectoryConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.DYNAMIC_WORKFLOW_CONFIG}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.DYNAMIC_WORKFLOW_CONFIG_SEARCH}
            >
              <DynamicWorkflowConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.BUILDINGS_CONFIGURATION}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.BUILDINGS_SEARCH_CONFIGURATION}
            >
              <BuildingsConfiguration />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_HELP_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EXTERNAL_HELP_CONFIG_SEARCH}>
              <ExternalHelpConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.CRITICAL_NATIONALITIES_CONFIG}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.CRITICAL_NATIONALITIES_CONFIG_SEARCH}
            >
              <CriticalNationalitiesConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.DEVICES_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.FR_DEVICES_CONFIG_SEARCH}>
              <FRDevicesConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.FR_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.FR_CONFIG_SEARCH}>
              <FRConfig />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.MUSTER_POINTS_CONFIG}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.MUSTER_POINTS_CREATE}>
              <MusterPointsConfiguration />
            </ProtectedRoute>
          }
        />

        {/* Email Templates */}
        <Route
          path={APP_ROUTES.VISIT_APPROVED_HOST_EMAIL}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_APPROVED_HOST_EMAIL_SEARCH}
            >
              <VisitApprovedHostEmail />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_SUBMITTED}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISIT_SUBMITTED_SEARCH}>
              <VisitSubmitted />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_REQUEST_SCHEDULE_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_REQUEST_SCHEDULE_TEMPLATE_SEARCH}
            >
              <VisitRequestScheduleTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_REJECTED_HOST_EMAIL}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_REJECTED_HOST_EMAIL_SEARCH}
            >
              <VisitRejectedHostEmailTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.FINAL_PERMIT_APPROVAL_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.FINAL_PERMIT_APPROVAL_SEARCH}
            >
              <FinalPermitApprovalTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_REQUEST_REJECTED_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_REQUEST_REJECTED_TEMPLATE_SEARCH}
            >
              <VisitRequestRejectedTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EMAIL_TO_HOST_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.EMAIL_TO_HOST_TEMPLATE_SEARCH}
            >
              <EmailToHostTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_PASSWORD_RESET}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.EXTERNAL_PASSWORD_RESET_SEARCH}
            >
              <ExternalPasswordReset />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_HOST}
          element={
            <ProtectedRoute
              routeName={
                ROUTE_NAMES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_HOST_SEARCH
              }
            >
              <ExternalVisitRequestCancelToHost />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR}
          element={
            <ProtectedRoute
              routeName={
                ROUTE_NAMES.EXTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR_SEARCH
              }
            >
              <ExternalVisitRequestCancelToVisitor />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.INTERNAL_CANCEL_VISIT_REQUEST_TO_HOST}
          element={
            <ProtectedRoute
              routeName={
                ROUTE_NAMES.INTERNAL_CANCEL_VISIT_REQUEST_TO_HOST_SEARCH
              }
            >
              <InternalVisitRequestCancelToHost />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.INTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR}
          element={
            <ProtectedRoute
              routeName={
                ROUTE_NAMES.INTERNAL_CANCEL_VISIT_REQUEST_TO_VISITOR_SEARCH
              }
            >
              <InternalVisitRequestCancelToVisitor />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_ACCOUNT_REGISTRATION_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={
                ROUTE_NAMES.EXTERNAL_ACCOUNT_REGISTRATION_TEMPLATE_SEARCH
              }
            >
              <ExternalAccountRegistrationTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_REQUESTED_INFO_TO_STAFF}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_REQUESTED_INFO_TO_STAFF_SEARCH}
            >
              <DeviceRejectedToStaffTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISIT_REQUESTED_INFO_TO_VISITOR}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.VISIT_REQUESTED_INFO_TO_VISITOR_SEARCH}
            >
              <DeviceRejectedToVisitorTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISITOR_CHECK_IN}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISITOR_CHECK_IN_SEARCH}>
              <VisitorCheckInTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISITOR_CHECK_OUT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISITOR_CHECK_OUT_SEARCH}>
              <VisitorCheckOutTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.OTP_TEMPLATE}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.OTP_TEMPLATE_SEARCH}>
              <OTPTemplate />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EMERGENCY_EVACUATION_TEMPLATE}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_TEMPLATE_SEARCH}
            >
              <EmergencyEvacuationTemplate />
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path={APP_ROUTES.VISIT_REQUEST_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISIT_REQUEST_REPORT_SEARCH}>
              <VisitRequestReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.ACCESS_LOG_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.ACCESS_LOG_REPORT_SEARCH}>
              <AccessLogReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EXTERNAL_USERS_REPORT}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.EXTERNAL_USERS_REPORT_SEARCH}
            >
              <ExternalUsersReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.AUDIT_LOGS_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.AUDIT_LOGS_REPORT_SEARCH}>
              <AuditLogsReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.LOGIN_USER_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.LOGIN_USER_REPORT_SEARCH}>
              <LoginUserReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.VISITORS_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.VISITORS_REPORT_SEARCH}>
              <VisitorsReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.OVERSTAYED_VISITORS_REPORT}
          element={
            <ProtectedRoute
              routeName={ROUTE_NAMES.OVERSTAYED_VISITORS_REPORT_SEARCH}
            >
              <OverStayedVisitorsReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.FR_ERRORS_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.FR_ERRORS_REPORT_SEARCH}>
              <FrErrorsReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.RATING_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.RATING_REPORT_SEARCH}>
              <RatingsReport />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.BLACKLISTING_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.BLACKLISTING_REPORT_SEARCH}>
              <BlacklistingReport />
            </ProtectedRoute>
          }
        />

        {/* Emergency Evacuation */}
        <Route
          path={APP_ROUTES.LIVE_EVACUATION_DASHBOARD}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH}>
              <LiveEvacuationDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.START_EVACUATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_CREATE}>
              <StartEvacuation />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.UPDATE_EVACUATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_CREATE}>
              <StartEvacuation />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.MUSTER_STATION}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH}>
              <MusterStation />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EVACUATION_EVENTS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_SEARCH}>
              <EvacuationEvents />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.EVACUATION_REPORT}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.EMERGENCY_EVACUATION_REPORT_SEARCH}>
              <EvacuationReport />
            </ProtectedRoute>
          }
        />

        {/* Blacklist */}
        <Route
          path={APP_ROUTES.ADD_BLACKLIST_VISITOR}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.BLACKLIST_CREATE}>
              <AddBlacklistVisitor />
            </ProtectedRoute>
          }
        />

        <Route
          path={APP_ROUTES.SEARCH_BLACKLIST_VISITORS}
          element={
            <ProtectedRoute routeName={ROUTE_NAMES.BLACKLIST_SEARCH}>
              <SearchBlacklistVisitors />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
